/* c8 ignore start */
import { listen } from '../../../base/events';
import { FullscreenController } from '../../../base/fullscreen';
import { DEV_MODE } from '../../../global/env';
import { noop } from '../../../utils/unit';
/**
 * Extends the base `FullscreenController` with additional logic for handling fullscreen
 * on iOS Safari where the native Fullscreen API is not available (in this case it fallsback to
 * using the `VideoPresentationController`).
 *
 * @example
 * ```ts
 * import { LitElement } from 'lit';
 * import {
 *   FullscreenController,
 *   ScreenOrientationController,
 *   VideoPresentationController
 * } from '@vidstack/jelements';
 *
 * class MyElement extends LitElement {
 *   get videoElement() {
 *     return this.videoEl;
 *   }
 *
 *   fullscreenController = new VideoFullscreenController(
 *     this,
 *     new ScreenOrientationController(this),
 *     new VideoPresentationController(this),
 *   );
 *
 *   async requestFullscreen() {
 *     if (this.fullscreenController.isRequestingNativeFullscreen) {
 *       return super.requestFullscreen();
 *     }
 *
 *     return this.fullscreenController.requestFullscreen();
 *   }
 *
 *   async exitFullscreen() {
 *     return this.fullscreenController.exitFullscreen();
 *   }
 * }
 * ```
 */
export class VideoFullscreenController extends FullscreenController {
    constructor(host, screenOrientationController, _presentationController) {
        super(host, screenOrientationController);
        this._presentationController = _presentationController;
    }
    get isFullscreen() {
        return this.isSupportedNatively
            ? this.isNativeFullscreen
            : this._presentationController.isFullscreenMode;
    }
    get isSupported() {
        return this.isSupportedNatively || this.isSupportedOnSafari;
    }
    /**
     * Whether a fallback fullscreen API is available on Safari using presentation modes. This
     * is only used on iOS where the native fullscreen API is not available.
     *
     * @link https://developer.apple.com/documentation/webkitjs/htmlvideoelement/1631913-webkitpresentationmode
     */
    get isSupportedOnSafari() {
        return this._presentationController.isSupported;
    }
    async _makeEnterFullscreenRequest() {
        return this.isSupportedNatively
            ? super._makeEnterFullscreenRequest()
            : this._makeFullscreenRequestOnSafari();
    }
    async _makeFullscreenRequestOnSafari() {
        return this._presentationController.setPresentationMode('fullscreen');
    }
    async _makeExitFullscreenRequest() {
        return this.isSupportedNatively
            ? super._makeExitFullscreenRequest()
            : this._makeExitFullscreenRequestOnSafari();
    }
    async _makeExitFullscreenRequestOnSafari() {
        return this._presentationController.setPresentationMode('inline');
    }
    _addFullscreenChangeEventListener(handler) {
        if (this.isSupportedNatively) {
            return super._addFullscreenChangeEventListener(handler);
        }
        if (this.isSupportedOnSafari) {
            if (DEV_MODE) {
                this._logger.info('adding `vds-video-presentation-change` listener');
            }
            return listen(this._host, 'vds-video-presentation-change', this._handlePresentationModeChange.bind(this));
        }
        return noop;
    }
    _handlePresentationModeChange(event) {
        this._handleFullscreenChange(event);
    }
    _addFullscreenErrorEventListener(handler) {
        if (!this.isSupportedNatively)
            return noop;
        return super._addFullscreenErrorEventListener(handler);
    }
}
/* c8 ignore stop */
